import React, { useState, ReactElement } from "react";
import { useDispatch } from "react-redux";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import CustomAlert from "components/common/Alert";
import CustomButton from "components/common/Button";
import SearchableAutoComplete from "components/common/AutoComplete";
import { AlertType } from "components/common/Snackbar";

import { showSnackbar } from "store/actions/Utility";

import { CustomerUserRow, transferOwnership } from "services/customerOwnership";

import "./TransferOwnershipDialog.scss";

interface TransferOwnershipDialogProps {
  open: boolean;
  onClose: () => void;
  onTransferred: () => void;
  candidates?: CustomerUserRow[];
  newOwner?: CustomerUserRow | null;
}

const getDisplayName = (user: CustomerUserRow): string =>
  `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email || user.userId;

function TransferOwnershipDialog({
  open,
  onClose,
  onTransferred,
  candidates = [],
  newOwner = null,
}: TransferOwnershipDialogProps): ReactElement {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<CustomerUserRow | null>(null);

  const target = newOwner || selectedUser;

  const handleClose = () => {
    if (isLoading) return;
    setSelectedUser(null);
    onClose();
  };

  const handleTransfer = async () => {
    if (!target) return;
    setIsLoading(true);
    const response = await transferOwnership(target.userId).catch(() => undefined);
    setIsLoading(false);
    if (response?.ok) {
      dispatch(
        showSnackbar({
          type: AlertType.success,
          message: `${getDisplayName(target)} is now the account owner`,
          dispatch,
        })
      );
      onTransferred();
      setSelectedUser(null);
      onClose();
    } else {
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: response?.payload?.error?.[0]?.description || "Failed to transfer account ownership",
          dispatch,
        })
      );
    }
  };

  const dialogContent = (
    <DialogContent>
      {newOwner ? (
        <div className="para-body-m-regular">{getDisplayName(newOwner)} will become the account owner.</div>
      ) : (
        <SearchableAutoComplete
          name="newOwner"
          size="medium"
          label="New account owner"
          helperText=""
          isOptional={false}
          error={false}
          placeholder="Select an admin"
          options={candidates.map((user) => ({
            label: getDisplayName(user),
            value: user.userId,
            render: () => (
              <div className="transfer-ownership-option">
                <div className="para-body-m-regular">{getDisplayName(user)}</div>
                {user.email && <div className="para-body-s-regular">{user.email}</div>}
              </div>
            ),
          }))}
          value={selectedUser?.userId}
          onChange={(userId: string | null) => setSelectedUser(candidates.find((user) => user.userId === userId) || null)}
        />
      )}
      {!newOwner && selectedUser?.email && (
        <div className="para-body-s-regular transfer-ownership-email">{selectedUser.email}</div>
      )}
      {newOwner?.firstLogin && (
        <div className="mt-1">
          <CustomAlert
            severity="warning"
            variant="standard"
            description={`${getDisplayName(newOwner)} has not activated their account yet.`}
          />
        </div>
      )}
      <div className="para-body-m-regular mt-1">
        The new owner becomes the billing contact for this account. The current owner stays a customer admin.
      </div>
    </DialogContent>
  );

  const dialogActions = (
    <DialogActions
      action1={
        <CustomButton
          label="Cancel"
          onClick={handleClose}
          size="large"
          color="primary"
          variant="text"
          isDisabled={isLoading}
        />
      }
      action2={
        <CustomButton
          label="Transfer Ownership"
          onClick={handleTransfer}
          size="large"
          color="primary"
          isLoading={isLoading}
          variant="contained"
          isDisabled={!target}
          className="transfer-ownership-submit"
        />
      }
    />
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={<DialogTitle title="Transfer account ownership" />}
      content={dialogContent}
      actions={dialogActions}
      size="sm"
    />
  );
}

export default TransferOwnershipDialog;
